import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api } from '../services/api';
import { useSelectSucursal } from '../hooks/useSelectSucursal';
import { useVendor } from '../context/VendorContext';
import {
  cityKey,
  filterSucursales,
  sameCity,
  sameZone,
  uniqueCities,
  uniqueZonas,
  zoneKey,
} from '../utils/sucursalFilters';
import { fixClienteDisplay, fixMojibakeText } from '../utils/textEncoding';
import ClienteSedesPanel from '../components/ClienteSedesPanel';

const SUCURSALES_CACHE_KEY = 'oh_sucursales_cache_v4';
const PAGE_SIZE = 80;

function rowKey(s, index) {
  return String(s.id ?? `row-${s.codigo}-${index}`);
}

function clearSucursalesCache() {
  try {
    localStorage.removeItem(SUCURSALES_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

/** Centra el chip activo en la tira horizontal (Zaragoza no debe “perderse” a la izquierda). */
function useScrollActiveChip(deps) {
  const rowRef = useRef(null);
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const active = row.querySelector('.mis-clientes-city-chip.active');
    if (!active || typeof active.scrollIntoView !== 'function') return;
    active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, deps);
  return rowRef;
}

export default function MisClientes({ onSelectForPedido, onToast, refreshKey = 0 }) {
  const { vendedor } = useVendor();
  const [all, setAll] = useState([]);
  const [total, setTotal] = useState(0);
  const [facetCities, setFacetCities] = useState([]);
  const [facetZonas, setFacetZonas] = useState([]);
  const [q, setQ] = useState('');
  const [ciudad, setCiudad] = useState('');
  const [zona, setZona] = useState('');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState('');
  const { selectSucursal, clearCliente, selectedCliente } = useSelectSucursal();
  const selectedSucursalCodigo = selectedCliente?.sucursal_codigo
    ? String(selectedCliente.sucursal_codigo)
    : '';
  const [expandedId, setExpandedId] = useState(null);

  function fetchPage({ searchQ = q, reset = true, nextOffset = 0 } = {}) {
    if (reset) setLoading(true);
    else setLoadingMore(true);
    return api
      .getSucursales({
        limit: PAGE_SIZE,
        offset: nextOffset,
        q: String(searchQ || '').trim() || undefined,
        ciudad: ciudad || undefined,
        zona: zona || undefined,
        facets: 1,
      })
      .then((r) => {
        const rows = (r.data || [])
          .map((s, i) => ({
            ...fixClienteDisplay(s),
            id: s.id != null ? s.id : rowKey(s, i),
          }))
          .filter((s) => !(s.es_sede || s.padre_id));
        setTotal(Number(r.total) || rows.length);
        setOffset(nextOffset + rows.length);
        setAll((prev) => (reset ? rows : [...prev, ...rows]));
        // No pisar el listado completo de ciudades al filtrar una (si no, la tira salta a “Todas”).
        if (!ciudad && Array.isArray(r.facets?.ciudades) && r.facets.ciudades.length) {
          setFacetCities(r.facets.ciudades.map(fixMojibakeText).filter(Boolean));
        }
        if (Array.isArray(r.facets?.zonas)) {
          setFacetZonas(r.facets.zonas.map(fixMojibakeText).filter(Boolean));
        } else if (reset && !ciudad) {
          setFacetZonas([]);
        }
        setLoadError('');
        try {
          if (reset && !searchQ && !ciudad && !zona && rows.length) {
            localStorage.setItem(SUCURSALES_CACHE_KEY, JSON.stringify(rows.slice(0, 500)));
          }
        } catch {
          /* ignore */
        }
        return rows;
      })
      .catch((err) => {
        if (!reset) {
          onToast?.(err.message || 'No se pudo cargar más', 'error');
          return [];
        }
        let cached = [];
        try {
          cached = JSON.parse(localStorage.getItem(SUCURSALES_CACHE_KEY) || '[]');
          if (!Array.isArray(cached)) cached = [];
        } catch {
          cached = [];
        }
        if (cached.length) {
          const filtered = filterSucursales(cached, { q: searchQ, ciudad, zona });
          setAll(filtered);
          setTotal(filtered.length);
          setOffset(filtered.length);
          setFacetCities(uniqueCities(cached));
          setFacetZonas(ciudad ? uniqueZonas(cached, ciudad) : []);
          setLoadError('Sin internet — mostrando clientes guardados en la tablet');
          onToast?.('Sin internet — clientes desde la tablet', 'ok');
        } else {
          setAll([]);
          setTotal(0);
          setOffset(0);
          setLoadError(err.message || 'No se pudieron cargar los clientes');
          onToast?.(err.message || 'No se pudieron cargar los clientes', 'error');
        }
        return cached;
      })
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  }

  function loadClientes(searchQ = q) {
    setOffset(0);
    return fetchPage({ searchQ, reset: true, nextOffset: 0 });
  }

  function loadMore() {
    if (loading || loadingMore || all.length >= total) return;
    fetchPage({ searchQ: q, reset: false, nextOffset: offset });
  }

  function refrescar() {
    clearSucursalesCache();
    loadClientes(q);
  }

  useEffect(() => {
    const t = setTimeout(() => loadClientes(q), q ? 500 : 0);
    return () => clearTimeout(t);
  }, [vendedor?.id, refreshKey, ciudad, zona, q]);

  const ciudades = useMemo(() => {
    if (facetCities.length) return facetCities;
    return uniqueCities(all);
  }, [facetCities, all]);

  const zonas = useMemo(() => {
    if (!ciudad) return [];
    if (facetZonas.length) return facetZonas;
    return uniqueZonas(all, ciudad);
  }, [ciudad, facetZonas, all]);

  const cityChipsRef = useScrollActiveChip([ciudad, ciudades.length, loading]);
  const zonaChipsRef = useScrollActiveChip([zona, zonas.length, ciudad]);

  const filtered = all;
  const hasMore = all.length < total;

  function handleTomarPedido(s, e) {
    e?.stopPropagation();
    selectSucursal(s);
    onSelectForPedido?.(s);
  }

  function handleCancelarCliente(e) {
    e?.stopPropagation();
    clearCliente();
    onToast?.('Cliente deseleccionado');
  }

  function toggleCiudad(name) {
    setCiudad((prev) => {
      const next = sameCity(prev, name) ? '' : name;
      setZona('');
      return next;
    });
  }

  function toggleZona(name) {
    setZona((prev) => (sameZone(prev, name) ? '' : name));
  }

  return (
    <section className="page mis-clientes-page">
      <div className="page-header">
        <div>
          <h1>Mis clientes</h1>
          <p className="page-subtitle">
            Filtra por ciudad o barrio.
          </p>
        </div>
        <div className="mis-clientes-header-actions">
          <button type="button" className="btn-ghost" onClick={refrescar} disabled={loading}>
            Actualizar
          </button>
          {!loading ? (
            <span className="mis-clientes-count">
              {filtered.length}
              {total > filtered.length ? ` de ${total}` : ''} clientes
            </span>
          ) : null}
        </div>
      </div>

      {selectedSucursalCodigo ? (
        <div className="mis-clientes-selected-bar">
          <div className="mis-clientes-selected-main">
            <span>
              {selectedCliente.es_sede || selectedCliente.padre_codigo
                ? 'Sucursal activa'
                : 'Cliente activo'}
            </span>
            <strong>
              {selectedCliente.es_sede || selectedCliente.padre_codigo ? (
                <>
                  <span className="cliente-sede-badge">Sucursal</span>{' '}
                  {selectedCliente.padre_codigo
                    ? `${selectedCliente.padre_codigo} · `
                    : ''}
                  {selectedCliente.sede_etiqueta || selectedCliente.nombre}
                </>
              ) : (
                <>
                  {selectedSucursalCodigo} · {selectedCliente.nombre}
                </>
              )}
            </strong>
          </div>
          <div className="mis-clientes-selected-actions">
            <button
              type="button"
              className="btn-ghost"
              onClick={handleCancelarCliente}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => onSelectForPedido?.({ nombre: selectedCliente.nombre })}
            >
              Ir al pedido
            </button>
          </div>
        </div>
      ) : null}

      {loadError ? (
        <p className="error banner">
          {loadError}{' '}
          <button type="button" className="link-btn" onClick={refrescar}>
            Reintentar
          </button>
        </p>
      ) : null}

      <div className="mis-clientes-toolbar">
        <input
          type="search"
          className="mis-clientes-search"
          placeholder="Buscar nombre, código, dirección, barrio..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {ciudad || zona || q ? (
          <button
            type="button"
            className="btn-ghost mis-clientes-clear"
            onClick={() => {
              setQ('');
              setCiudad('');
              setZona('');
            }}
          >
            Limpiar filtros
          </button>
        ) : null}
      </div>

      {!loading && ciudades.length ? (
        <div
          ref={cityChipsRef}
          className="mis-clientes-city-chips"
          role="listbox"
          aria-label="Filtrar por ciudad"
        >
          <button
            type="button"
            className={`mis-clientes-city-chip ${!ciudad ? 'active' : ''}`}
            onClick={() => {
              setCiudad('');
              setZona('');
            }}
          >
            Todas
          </button>
          {ciudades.map((c) => (
            <button
              key={cityKey(c)}
              type="button"
              className={`mis-clientes-city-chip ${sameCity(ciudad, c) ? 'active' : ''}`}
              onClick={() => toggleCiudad(c)}
            >
              {c}
            </button>
          ))}
        </div>
      ) : null}

      {ciudad && zonas.length ? (
        <div
          ref={zonaChipsRef}
          className="mis-clientes-city-chips mis-clientes-zona-chips"
          role="listbox"
          aria-label="Filtrar por barrio"
        >
          <button
            type="button"
            className={`mis-clientes-city-chip ${!zona ? 'active' : ''}`}
            onClick={() => setZona('')}
          >
            Todos los barrios
          </button>
          {zonas.map((z) => (
            <button
              key={zoneKey(z)}
              type="button"
              className={`mis-clientes-city-chip ${sameZone(zona, z) ? 'active' : ''}`}
              onClick={() => toggleZona(z)}
            >
              {z}
            </button>
          ))}
        </div>
      ) : null}

      {ciudad || zona ? (
        <p className="mis-clientes-filter-hint">
          Mostrando {filtered.length}
          {total > filtered.length ? ` de ${total}` : ''} cliente
          {total === 1 ? '' : 's'}
          {ciudad ? (
            <>
              {' '}
              en <strong>{ciudad}</strong>
            </>
          ) : null}
          {zona ? (
            <>
              {' '}
              · <strong>{zona}</strong>
            </>
          ) : null}
        </p>
      ) : null}

      {loading ? <p className="loading-grid">Cargando clientes...</p> : null}

      {!loading && !filtered.length ? (
        <div className="mis-clientes-empty">
          <p className="empty-state">
            No hay clientes{q || ciudad || zona ? ' con ese filtro' : ''}.
            {!q && !ciudad && !zona
              ? ' Pide al administrador que importe o cree clientes.'
              : ' Prueba otra ciudad o barrio.'}
          </p>
        </div>
      ) : null}

      <div className="mis-clientes-list" key={`lista-${cityKey(ciudad)}-${zoneKey(zona)}-${q}`}>
        {filtered.map((s, index) => {
          const codigo = String(s.codigo);
          const isSelected = selectedSucursalCodigo === codigo;
          const sedesCount = Number(s.sedes_count) || 0;
          const isPadre = !s.es_sede && !s.padre_id;
          return (
            <article
              key={rowKey(s, index)}
              className={`data-card mis-cliente-card ${isSelected ? 'selected' : ''} ${
                expandedId === s.id ? 'sedes-open' : ''
              }`}
            >
              <div className="mis-cliente-card-main">
                <div className="mis-cliente-card-body">
                  {isSelected ? <span className="mis-cliente-check">✓ Seleccionado</span> : null}
                  {s.es_sede || s.padre_id ? (
                    <span className="cliente-sede-badge">Sucursal</span>
                  ) : (
                    <span className="cliente-padre-badge">Cliente</span>
                  )}
                  <span className="cliente-code">Cód. {codigo}</span>
                  <strong>{s.display_nombre || s.nombre}</strong>
                  <span className="mis-cliente-ciudad">
                    {s.ciudad}
                    {s.zona ? ` · ${s.zona}` : ''}
                  </span>
                  {s.direccion ? <span>{s.direccion}</span> : null}
                  {s.telefono1 || s.telefono ? (
                    <span className="muted">Tel. {s.telefono1 || s.telefono}</span>
                  ) : null}
                  {s.vendedor_nombre ? (
                    <span className="muted">Vendedor: {s.vendedor_nombre}</span>
                  ) : null}
                </div>
                <div className="data-card-actions">
                  {isPadre ? (
                    <button
                      type="button"
                      className={`btn-ghost mis-cliente-sedes-btn${
                        sedesCount <= 0 && expandedId !== s.id ? ' sede-add-hint' : ''
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedId((prev) => (prev === s.id ? null : s.id));
                      }}
                    >
                      {expandedId === s.id
                        ? 'Ocultar sedes'
                        : sedesCount > 0
                          ? sedesCount === 1
                            ? '1 sede'
                            : `${sedesCount} sedes`
                          : '+ Sede'}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn-primary mis-cliente-pedido-btn"
                    onClick={(e) => handleTomarPedido(s, e)}
                  >
                    Tomar pedido
                  </button>
                </div>
              </div>
              {expandedId === s.id && !s.es_sede && !s.padre_id ? (
                <ClienteSedesPanel
                  cliente={s}
                  onToast={onToast}
                  onSelectSede={(sede) => {
                    handleTomarPedido(sede);
                    setExpandedId(null);
                  }}
                  onSedesChanged={(delta) => {
                    setAll((prev) =>
                      prev.map((row) => {
                        if (Number(row.id) !== Number(s.id)) return row;
                        const next = Math.max(0, (Number(row.sedes_count) || 0) + (delta || 0));
                        return { ...row, sedes_count: next };
                      })
                    );
                  }}
                />
              ) : null}
            </article>
          );
        })}
      </div>

      {!loading && hasMore ? (
        <div className="mis-clientes-more">
          <button type="button" className="btn-ghost" disabled={loadingMore} onClick={loadMore}>
            {loadingMore ? 'Cargando…' : `Cargar más (${all.length} de ${total})`}
          </button>
        </div>
      ) : null}
    </section>
  );
}
